import {
    EVENTBRITE_CATEGORY_TO_REFINEMENT,
    FORMATTED_SUBCATEGORY_MAP,
    getParentCategoryFromSubCategory,
    isSubCategory,
} from '@eventbrite/categories';
import { getCanonicalSearchUrl } from '@eventbrite/redux-destination';
import { OrganicEventSearchFiltersApiPayload } from '../../../api/searchOrganicEvents';
import { DateRange, DateType } from '../../../constants/dates';
import { FormattedLocationResponse } from '../api';

/**
 * Grabs the appropriate filters from the URL. These include:
 *
 * tag: The active tag to filter against
 * date: String representation of the date (today, tomorrow, etc)
 * start_date: Custom date range in the YYYY-MM-DD format
 * end_date: Custom date range in the YYYY-MM-DD format
 *
 * @param params URL Params
 * @returns
 */
export const parseEventSearchFromUrlParams = (params: URLSearchParams) => {
    const activeTag = params.get('tag') || '';
    const date = (params.get('date') as DateType) || 'current_future';
    const customDate =
        params.has('start_date') && params.has('end_date')
            ? ({
                  from: params.get('start_date') || undefined,
                  to: params.get('end_date') || undefined,
              } as DateRange)
            : undefined;

    return {
        activeTag,
        date,
        customDate,
    };
};

/**
 * Returns a properly formatted search url.
 *
 * @param currentQuery OrganicEventSearchFiltersApiPayload from SearchContext
 * @param location FormattedLocation Response from useLocation Hook
 * @returns
 */
export const generateSearchUrl = (
    currentQuery: OrganicEventSearchFiltersApiPayload,
    location: FormattedLocationResponse,
): string => {
    const currentQueryPath = getCanonicalSearchUrl({
        slug: currentQuery?.online_events_only ? 'online' : location.slug,
        price: currentQuery.price,
        tags: currentQuery.tags,
        dates: currentQuery.dates,
        q: currentQuery.q,
        bbox: currentQuery.bbox,
    });

    const queryPathWithPage = currentQueryPath.includes('?')
        ? `${currentQueryPath}&page=1`
        : `${currentQueryPath}?page=1`;

    if (currentQuery.date_range) {
        const customDateString = `start_date=${currentQuery.date_range.from}&end_date=${currentQuery.date_range.to}`;

        return `${queryPathWithPage}&${customDateString}`;
    }

    return `${queryPathWithPage}`;
};

/**
 * Generates an appropriate browse URL for relative
 * linking across pages. Will parameterize tag, date,
 * and location information.
 */
export const generateBrowseUrl = (
    currentQuery: OrganicEventSearchFiltersApiPayload,
    location: FormattedLocationResponse,
) => {
    let path = `/b/${location.slug}/`;
    const params = new URLSearchParams();

    if (currentQuery.tags && currentQuery.tags.length) {
        const tag = currentQuery.tags[0];
        if (isSubCategory(tag)) {
            const parentCategory = getParentCategoryFromSubCategory(tag);
            const categoryRefinement =
                EVENTBRITE_CATEGORY_TO_REFINEMENT[parentCategory];
            const { refinement: subcategoryRefinement } =
                FORMATTED_SUBCATEGORY_MAP[tag];

            path = `${path}${categoryRefinement}/${subcategoryRefinement}/`;
        } else {
            const categoryRefinement = EVENTBRITE_CATEGORY_TO_REFINEMENT[tag];
            path = `${path}${categoryRefinement}/`;
        }
    }

    if (
        currentQuery.dates &&
        !onlyHasCurrentFutureDate(currentQuery) &&
        typeof currentQuery.dates !== 'string'
    ) {
        params.set('date', currentQuery.dates[1]);
    }

    if (currentQuery.date_range?.from && currentQuery.date_range.to) {
        params.set('start_date', currentQuery.date_range.from);
        params.set('end_date', currentQuery.date_range.to);
    }

    return `${path}?${params.toString()}`;
};

function onlyHasCurrentFutureDate(
    currentQuery: OrganicEventSearchFiltersApiPayload,
): boolean {
    if (!currentQuery.dates) {
        return false;
    }
    return (
        currentQuery.dates.length === 1 &&
        currentQuery.dates[0] === 'current_future'
    );
}
